import type { IFreeSlotItem, IMatchScheduleItem, IScheduleItem } from '../../../../../types/schedule';
import { getScheduleLaidOutRowHeightPx } from '../../../../../features/schedule/scheduleZoomBounds';

const FREE_SLOT_MINUTES = 5;
const MS_PER_MINUTE = 60000;

const isCourtDropZoneData = (
  data: unknown
): data is {
  isCourtDropZone: true;
  day: number;
  court: IFreeSlotItem['court'];
  dayStartMs: number;
  dayEndMs: number;
  scale: number;
} => typeof data === 'object' && data !== null && 'isCourtDropZone' in data;

export const resolveDropTargetData = (event: any): IScheduleItem | IFreeSlotItem | null => {
  const { target, position } = event.operation;
  const targetData = target?.data;

  if (!isCourtDropZoneData(targetData)) {
    return targetData || null;
  }

  const targetElement = target?.element;
  const targetRect =
    targetElement && typeof targetElement.getBoundingClientRect === 'function'
      ? targetElement.getBoundingClientRect()
      : null;
  const pointerY = position?.current?.y;

  if (!targetRect || typeof pointerY !== 'number') {
    return null;
  }

  const rowHeight = getScheduleLaidOutRowHeightPx(targetData.scale);
  const maxSlotIndex = Math.max(
    0,
    Math.ceil((targetData.dayEndMs - targetData.dayStartMs) / (FREE_SLOT_MINUTES * MS_PER_MINUTE)) -
      1
  );
  const slotIndex = Math.max(
    0,
    Math.min(maxSlotIndex, Math.floor((pointerY - targetRect.top) / rowHeight))
  );
  const scheduleDateMs = targetData.dayStartMs + slotIndex * FREE_SLOT_MINUTES * MS_PER_MINUTE;

  return {
    isFreeSlot: true,
    isScheduled: true,
    day: targetData.day,
    court: targetData.court,
    scheduleDate: new Date(scheduleDateMs).toISOString(),
    duration: FREE_SLOT_MINUTES,
  } as IFreeSlotItem;
};

export const resolveMatchFromDnDData = (
  data: unknown,
  scheduleFlatMatches: IMatchScheduleItem[],
  unscheduledMatches: IMatchScheduleItem[]
): IMatchScheduleItem | undefined => {
  if (typeof data !== 'object' || data === null || !('id' in data)) return undefined;
  const id = Number((data as { id: unknown }).id);
  if (!Number.isFinite(id)) return undefined;
  return (
    scheduleFlatMatches.find((m) => m.id === id) ?? unscheduledMatches.find((m) => m.id === id)
  );
};
